/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import { ServiceAreasHero } from '../components/ServiceAreasHero';
import { WhyChooseUs } from '../components/WhyChooseUs';
import { ReadyToShopCta } from '../components/ReadyToShopCta';
import { NearbyAreasSection } from '../components/NearbyAreasSection';
import { useSEOHead } from '../../../hooks/useSEOHead';
import { injectJsonLd, removeJsonLd, buildBreadcrumbSchema, buildWebPageSchema } from '../../../lib/schemaBuilder';

export default function LongBeachWAPage() {
  useSEOHead({
    title: 'Used Cars Long Beach WA — No Dealership Drama | Rockhound Auto',
    description: 'Quality used cars delivered free to Long Beach, WA. No dealership pressure, transparent pricing, and every vehicle supports Oregon Dog Rescue. Shop stress-free from the coast.',
    keywords: 'used cars Long Beach WA, Long Beach Washington auto sales, free car delivery Long Beach, coastal car buying, Pacific County vehicles, Washington used cars',
    canonicalUrl: 'https://rockhoundauto.com/service-areas/long-beach-wa',
    geoRegion: 'US-WA',
    geoPlacename: 'Long Beach, Washington'
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Service Areas',
          item: 'https://rockhoundauto.com/service-areas',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Long Beach WA',
          item: 'https://rockhoundauto.com/service-areas/long-beach-wa',
        },
      ],
    };

    const webPageSchema = buildWebPageSchema({
      name: 'Used Cars in Long Beach Washington | Rockhound Auto',
      description: 'Quality used cars delivered free to Long Beach, WA. No dealership pressure, transparent pricing, and every vehicle supports Oregon Dog Rescue.',
      url: 'https://rockhoundauto.com/service-areas/long-beach-wa'
    });

    injectJsonLd('schema-long-beach-wa-breadcrumb', breadcrumbSchema);
    injectJsonLd('schema-long-beach-wa-webpage', webPageSchema);

    return () => {
      removeJsonLd('schema-long-beach-wa-breadcrumb');
      removeJsonLd('schema-long-beach-wa-webpage');
    };
  }, []);

  const nearbyAreas = [
    { name: 'Ilwaco', slug: 'ilwaco' },
    { name: 'Astoria', slug: 'astoria' },
    { name: 'Seaside', slug: 'seaside' },
    { name: 'Warrenton', slug: 'warrenton' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <ServiceAreasHero
        city="Long Beach"
        state="Washington"
        tagline="Buy a Car Without the Dealership Drama"
        description="We deliver quality used cars straight to Long Beach, WA — no pushy sales tactics, no hidden fees, just honest deals and free delivery. Plus, every purchase helps rescue dogs through Oregon Dog Rescue."
      />

      <WhyChooseUs city="Long Beach" />

      <section className="py-16 bg-gradient-to-b from-white to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              Why Long Beach Buyers Choose Rockhound Auto
            </h2>
            <p className="text-lg text-stone-600">
              Because driving to a dealership shouldn't feel like a hostage negotiation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-stone-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-truck-line text-2xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Free Delivery to Long Beach</h3>
              <p className="text-stone-700">
                We bring the car to you — whether you're near the boardwalk or tucked away in Seaview. No need to drive to Warrenton or Portland.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-stone-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Coastal-Ready Vehicles</h3>
              <p className="text-stone-700">
                Every vehicle is inspected for rust, salt damage, and coastal wear. We know what holds up in Pacific County weather.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-stone-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-heart-line text-2xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Every Sale Helps Rescue Dogs</h3>
              <p className="text-stone-700">
                A portion of every purchase goes to Oregon Dog Rescue. Buy a car, save a pup. It's that simple.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              What Makes Long Beach Different?
            </h2>
            <p className="text-lg text-stone-600">
              You live in a place where the beach is 28 miles long and the nearest big dealership is an hour away. You deserve better than a road trip just to get pressured into a bad deal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-stone-900 mb-3">🌊 Coastal Weather Matters</h3>
              <p className="text-stone-700">
                Salt air, rain, and humidity are tough on cars. We inspect every vehicle for rust, undercarriage damage, and weatherproofing before delivery.
              </p>
            </div>

            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-stone-900 mb-3">🚗 No Dealership Pressure</h3>
              <p className="text-stone-700">
                No finance managers, no "let me talk to my manager," no four-hour negotiations. Just transparent pricing and honest answers.
              </p>
            </div>

            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-stone-900 mb-3">📍 Local Knowledge</h3>
              <p className="text-stone-700">
                We know the roads between Long Beach and Astoria. We know what vehicles hold up in Pacific County. We're not some faceless online dealer.
              </p>
            </div>

            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-stone-900 mb-3">🐕 Community First</h3>
              <p className="text-stone-700">
                Every purchase supports Oregon Dog Rescue. Because helping dogs {'>'} helping dealership shareholders.
              </p>
            </div>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Long Beach" nearbyAreas={nearbyAreas} />

      <ReadyToShopCta cityName="Long Beach" />
    </div>
  );
}

export { LongBeachWAPage };
